/**
 * useEpisodeTodo — session todo state for one chat context (plan DEC-9).
 *
 * Hydrates from `episodes.todo.get` when an episode opens, then live-updates
 * from `onTodo` snapshots (accepted `episodes.todo.update` results). A live
 * snapshot always wins over a slower hydration response.
 */
import { useEffect, useRef, useState } from "react";
import type { TodoItem, TodoSnapshot } from "@magnis/client-core";
import type { AppRuntime } from "../../runtime/contracts/runtime";

export function useEpisodeTodo(
  runtime: AppRuntime,
  contextKey: string,
  episodeId: string | null,
  enabled: boolean,
): readonly TodoItem[] {
  const [items, setItems] = useState<readonly TodoItem[]>([]);
  const liveSeenRef = useRef(false);
  const episodeRef = useRef<string | null>(episodeId);
  const [lastEpisodeId, setLastEpisodeId] = useState<string | null>(episodeId);

  if (lastEpisodeId !== episodeId) {
    setLastEpisodeId(episodeId);
    episodeRef.current = episodeId;
    liveSeenRef.current = false;
    setItems([]);
  }

  // Live snapshots.
  useEffect(() => {
    if (!enabled) return;
    return runtime.agent.chat.onTodo((raw) => {
      const snapshot = raw as TodoSnapshot;
      if (snapshot.contextKey !== contextKey) return;
      // Review fix (C6): while we track an episode, only snapshots stamped
      // with THAT episode id count — a null-episode snapshot must not set
      // liveSeen and suppress the real hydration.
      if (episodeRef.current && snapshot.episodeId !== episodeRef.current) return;
      liveSeenRef.current = true;
      setItems(snapshot.items);
    });
  }, [enabled, runtime, contextKey]);

  // Hydration on episode open (INV-9).
  useEffect(() => {
    if (!enabled || !episodeId) return;
    let cancelled = false;
    runtime.transport
      .rpc<{ items: readonly TodoItem[] }>("episodes.todo.get", {
        episode_id: episodeId,
      })
      .then((result): void => {
        if (cancelled || liveSeenRef.current) return;
        if (episodeRef.current !== episodeId) return;
        setItems(result.items);
      })
      .catch((error: unknown) => {
        // A brand-new episode returns { items: [] } successfully — reaching
        // here means a REAL error. Surface it; the block stays hidden.
        console.warn("todo hydration failed:", error);
      });
    return (): void => {
      cancelled = true;
    };
  }, [enabled, episodeId, runtime]);

  return items;
}
